import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";
import { Card, Nav } from "react-bootstrap";
import { getCompanyNews } from "../apis/CompanyNews";
import { buildNewsMeta, buildSnippet, CATEGORY_DEFINITIONS } from "../utils/homeUtils";
import { AdUnit } from "../components/AdComponent";

export const NEWS_CATEGORY_ITEMS = [
  { key: "all", label: "전체" },
  ...CATEGORY_DEFINITIONS.map((category) => ({
    key: category.key,
    label: category.label,
  })),
];

const INITIAL_IMPORTANT_VISIBLE = 5;
const IMPORTANT_INCREMENT = 5;
const INITIAL_CATEGORY_VISIBLE = 10;
const CATEGORY_INCREMENT = 10;
const SIDE_NEWS_LIMIT = 8;
const SIDE_TABS = [
  { key: "good", label: "Good News", headerClassName: "text-success" },
  { key: "bad", label: "Bad News", headerClassName: "text-danger" },
];
const AVAILABLE_CATEGORY_KEYS = new Set(NEWS_CATEGORY_ITEMS.map((tab) => tab.key));

const formatDisplayDateTime = (value) => (value ? String(value).replace("T", " ") : "");
const oneLineClampStyle = {
  display: "-webkit-box",
  WebkitBoxOrient: "vertical",
  WebkitLineClamp: 1,
  overflow: "hidden",
};

const toNewsItems = (payload) => {
  const source = Array.isArray(payload) ? payload : payload?.data || [];
  return source
    .map((item) => {
      const meta = buildNewsMeta(item);
      return {
        ...meta,
        documentid: item.documentid,
        form: item.form || "",
        importance: Number.isFinite(Number(item.importance)) ? Number(item.importance) : null,
        goodorbad: String(item.goodorbad || "").toLowerCase(),
      };
    })
    .sort((a, b) => b.timestamp - a.timestamp);
};

const buildSections = (newsItems) => {
  const importantSection = {
    key: "important",
    label: "중요한 뉴스",
    items: newsItems.filter((item) => item.importance === 3),
  };

  const categorySections = CATEGORY_DEFINITIONS.map((category) => ({
    key: category.key,
    label: category.label,
    items: newsItems.filter((item) => item.categoryKey === category.key),
  }));

  const sentimentSections = [
    {
      key: "good",
      label: "Good News",
      items: newsItems.filter((item) => item.importance === 3 && item.goodorbad === "good"),
    },
    {
      key: "bad",
      label: "Bad News",
      items: newsItems.filter((item) => item.importance === 3 && item.goodorbad === "bad"),
    },
  ];

  return [importantSection, ...categorySections, ...sentimentSections];
};

export default function News() {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("all");
  const [activeSideTab, setActiveSideTab] = useState("good");
  const [visibleImportantCount, setVisibleImportantCount] = useState(INITIAL_IMPORTANT_VISIBLE);
  const [visibleCategoryCount, setVisibleCategoryCount] = useState(INITIAL_CATEGORY_VISIBLE);

  const filterParam = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("filter");
  }, [location.search]);

  const dateParam = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("date");
  }, [location.search]);

  const companyNewsQuery = useQuery({
    queryKey: ["companyNews", "list", dateParam],
    queryFn: async () => {
      const response = await getCompanyNews(null, 500, dateParam, dateParam);

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      return response.json();
    },
  });

  const newsItems = useMemo(() => toNewsItems(companyNewsQuery.data), [companyNewsQuery.data]);
  const sections = useMemo(() => buildSections(newsItems), [newsItems]);
  const sectionMap = useMemo(() => {
    return sections.reduce((acc, section) => {
      acc[section.key] = section;
      return acc;
    }, {});
  }, [sections]);
  const cards = useMemo(() => {
    return NEWS_CATEGORY_ITEMS.map((tab) => ({
      ...tab,
      items: tab.key === "all" ? newsItems : sectionMap[tab.key]?.items || [],
    }));
  }, [newsItems, sectionMap]);

  const importantItems = useMemo(() => sectionMap.important?.items || [], [sectionMap]);
  const goodItems = useMemo(() => sectionMap.good?.items || [], [sectionMap]);
  const badItems = useMemo(() => sectionMap.bad?.items || [], [sectionMap]);
  const activeSideItems = activeSideTab === "bad" ? badItems : goodItems;
  const resolvedActiveTab = useMemo(() => {
    return cards.some((card) => card.key === activeTab) ? activeTab : "all";
  }, [activeTab, cards]);
  const activeCard = useMemo(
    () =>
      cards.find((card) => card.key === resolvedActiveTab) ||
      cards.find((card) => card.key === "all") ||
      null,
    [resolvedActiveTab, cards]
  );

  const handleNewsItemClick = (item) => {
    if (!item?.documentid) {
      return;
    }

    navigate(`/news/${item.documentid}`, {
      state: {
        newsItem: item,
      },
    });
  };

  useEffect(() => {
    document.body.classList.add("page-app");
    return () => {
      document.body.classList.remove("page-app");
    };
  }, []);

  useEffect(() => {
    if (filterParam && AVAILABLE_CATEGORY_KEYS.has(filterParam)) {
      setActiveTab(filterParam);
      return;
    }
    setActiveTab("all");
  }, [filterParam]);

  useEffect(() => {
    setVisibleCategoryCount(INITIAL_CATEGORY_VISIBLE);
    setVisibleImportantCount(INITIAL_IMPORTANT_VISIBLE);
  }, [resolvedActiveTab, dateParam]);

  return (
    <div className="p-4 p-lg-5" style={{ maxWidth: "1280px", margin: "0 auto" }}>
      <h2 className="main-title mb-4">간밤의 주요이슈</h2>

      <div className="news-section">
        <section className="category">
          <Card>
            <Card.Header className="fw-semibold">Category</Card.Header>
            <Card.Body className="category-list">
              {cards.map((item) => {
                const isActive = item.key === resolvedActiveTab;
                return (
                  <button
                    key={item.key}
                    type="button"
                    className={`btn fw-semibold text-start ${isActive ? "btn-primary" : "btn-outline-secondary"}`}
                    onClick={() => setActiveTab(item.key)}
                  >
                    <span>{item.label}</span>
                    <span className="float-end ps-2">{item.items.length}</span>
                  </button>
                );
              })}
            </Card.Body>
          </Card>
        </section>

        <section className="content">
          <div className="d-flex flex-column gap-3">
            {companyNewsQuery.isLoading ? (
              <Card>
                <Card.Body className="text-center text-secondary py-5">
                  뉴스를 불러오는 중입니다.
                </Card.Body>
              </Card>
            ) : companyNewsQuery.isError ? (
              <Card>
                <Card.Body className="text-center text-secondary py-5">
                  뉴스를 불러오지 못했습니다.
                </Card.Body>
              </Card>
            ) : (
              <>
                {resolvedActiveTab === "all" && (
                  <Card>
                    <Card.Header className="fw-semibold">중요한 뉴스</Card.Header>
                    <Card.Body className="d-flex flex-column gap-3">
                      {importantItems.length ? (
                        <>
                          {importantItems.slice(0, visibleImportantCount).map((item) => (
                            <article
                              key={`important-${item.documentid}`}
                              className="border rounded-3 p-2"
                              role="button"
                              tabIndex={0}
                              style={{ cursor: "pointer" }}
                              onClick={() => handleNewsItemClick(item)}
                              onKeyDown={(event) => {
                                if (event.key === "Enter" || event.key === " ") {
                                  event.preventDefault();
                                  handleNewsItemClick(item);
                                }
                              }}
                            >
                              <div className="d-flex align-items-start justify-content-between gap-3 mb-1">
                                <h6 className="mb-0 flex-grow-1" style={oneLineClampStyle}>
                                  {item.title}
                                </h6>
                                <span className="text-secondary fs-xs text-nowrap">
                                  {formatDisplayDateTime(item.reportdate)}
                                </span>
                              </div>
                              <p className="mb-1 text-secondary fs-sm" style={oneLineClampStyle}>
                                {buildSnippet(item.body, 180)}
                              </p>
                              <div className="d-flex justify-content-between align-items-center gap-2">
                                <span className="text-dark">{item.source}</span>
                                <span className="text-secondary fs-xs">{item.ticker}</span>
                              </div>
                            </article>
                          ))}
                          {importantItems.length > visibleImportantCount && (
                            <button
                              type="button"
                              className="btn btn-outline-secondary fw-semibold"
                              onClick={() => setVisibleImportantCount((prev) => prev + IMPORTANT_INCREMENT)}
                            >
                              뉴스 더보기
                            </button>
                          )}
                        </>
                      ) : (
                        <div className="text-secondary">중요한 뉴스가 없습니다.</div>
                      )}
                    </Card.Body>
                  </Card>
                )}
                <div className="mt-3 p-3">
                  <AdUnit key="sidebar-ad" slot="1213002757" style={{}} />
                </div>
                <Card className="h-100">
                  <Card.Header className="fw-semibold">
                    {activeCard?.label || "카테고리 뉴스"}
                  </Card.Header>
                  <Card.Body className="d-flex flex-column gap-3">
                    {activeCard?.items?.length ? (
                      <>
                        {activeCard.items.slice(0, visibleCategoryCount).map((item) => (
                          <article
                            key={`${activeCard.key}-${item.documentid}`}
                            className="border rounded-3 p-2"
                            role="button"
                            tabIndex={0}
                            style={{ cursor: "pointer" }}
                            onClick={() => handleNewsItemClick(item)}
                            onKeyDown={(event) => {
                              if (event.key === "Enter" || event.key === " ") {
                                event.preventDefault();
                                handleNewsItemClick(item);
                              }
                            }}
                          >
                            <div className="d-flex align-items-start justify-content-between gap-3 mb-1">
                              <h6 className="mb-0 flex-grow-1" style={oneLineClampStyle}>
                                {item.title}
                              </h6>
                              <span className="text-secondary fs-xs text-nowrap">
                                {formatDisplayDateTime(item.reportdate)}
                              </span>
                            </div>
                            <p className="mb-1 text-secondary fs-sm" style={oneLineClampStyle}>
                              {buildSnippet(item.body, 160)}
                            </p>
                            <div className="d-flex justify-content-between align-items-center gap-2">
                              <span className="text-dark">{item.source}</span>
                              <span className="text-secondary fs-xs">{item.ticker}</span>
                            </div>
                          </article>
                        ))}
                        {activeCard.items.length > visibleCategoryCount && (
                          <button
                            type="button"
                            className="btn btn-outline-secondary fw-semibold"
                            onClick={() => setVisibleCategoryCount((prev) => prev + CATEGORY_INCREMENT)}
                          >
                            뉴스 더보기
                          </button>
                        )}
                      </>
                    ) : (
                      <div className="text-secondary">뉴스가 없습니다.</div>
                    )}
                  </Card.Body>
                </Card>
              </>
            )}
          </div>
        </section>

        <aside className="sub-content">
          <Card>
            <Card.Header className="border-0 bg-transparent pb-0">
              <Nav
                activeKey={activeSideTab}
                className="nav-line side-news-tabs flex-nowrap overflow-x-auto overflow-y-hidden nav-scroll-hide align-items-center"
              >
                {SIDE_TABS.map((tab) => {
                  const isActive = tab.key === activeSideTab;
                  return (
                    <Nav.Item key={tab.key}>
                      <Nav.Link
                        eventKey={tab.key}
                        as="button"
                        type="button"
                        className={isActive ? "active border-bottom" : ""}
                        active={isActive}
                        onClick={() => setActiveSideTab(tab.key)}
                      >
                        {tab.label}
                      </Nav.Link>
                    </Nav.Item>
                  );
                })}
                <span
                  className={`side-news-count ms-auto flex-shrink-0 ${SIDE_TABS.find((tab) => tab.key === activeSideTab)?.headerClassName || ""
                    }`}
                >
                  {activeSideItems.length}건
                </span>
              </Nav>
            </Card.Header>
            <Card.Body className="d-flex flex-column gap-3">
              {activeSideItems.length ? (
                activeSideItems.slice(0, SIDE_NEWS_LIMIT).map((item) => (
                  <article
                    key={`${activeSideTab}-${item.documentid}`}
                    className="border rounded-3 p-2"
                    role="button"
                    tabIndex={0}
                    style={{ cursor: "pointer" }}
                    onClick={() => handleNewsItemClick(item)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        handleNewsItemClick(item);
                      }
                    }}
                  >
                    <h6 className="mb-1" style={oneLineClampStyle}>{item.title}</h6>
                    <div className="text-secondary fs-xs mb-1">
                      {formatDisplayDateTime(item.reportdate)}
                    </div>
                    <p className="mb-0 text-secondary fs-sm" style={oneLineClampStyle}>
                      {buildSnippet(item.body, 110)}
                    </p>
                  </article>
                ))
              ) : (
                <div className="text-secondary">
                  {activeSideTab === "good" ? "Good News가 없습니다." : "Bad News가 없습니다."}
                </div>
              )}
            </Card.Body>
          </Card>
          <div className="mt-3 p-3">
            <AdUnit key="sidebar-ad" slot="8470331234" style={{}} />
          </div>
        </aside>
      </div>
    </div>
  );
}
